import { TinyColor } from '@ctrl/tinycolor';
import { Logger } from '../services/logger.service';

const logger = new Logger('color');

export function darkenColor(color: string, percent: number): string {
    let r: number = 0, g: number = 0, b: number = 0, a: number = 1; // Default alpha is 1 (opaque)
    let inputFormat: 'hex' | 'hex-alpha' | 'rgb' | 'rgba' | 'invalid' = 'invalid';
  
    // Validate and parse hex color (6-digit)
    if (/^#[0-9A-Fa-f]{6}$/.test(color)) {
      inputFormat = 'hex';
      const hex: string = color.replace('#', '');
      r = parseInt(hex.slice(0, 2), 16);
      g = parseInt(hex.slice(2, 4), 16);
      b = parseInt(hex.slice(4, 6), 16);
    }
    // Validate and parse hex color with alpha (8-digit)
    else if (/^#[0-9A-Fa-f]{8}$/.test(color)) {
      inputFormat = 'hex-alpha';
      const hex: string = color.replace('#', '');
      r = parseInt(hex.slice(0, 2), 16);
      g = parseInt(hex.slice(2, 4), 16);
      b = parseInt(hex.slice(4, 6), 16);
      a = parseInt(hex.slice(6, 8), 16) / 255; // Convert alpha to [0, 1]
    }
    // Validate and parse RGB color
    else if (/^rgb\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})\)$/.test(color)) {
      inputFormat = 'rgb';
      const match = color.match(/^rgb\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})\)$/);
      if (match) {
        r = parseInt(match[1], 10);
        g = parseInt(match[2], 10);
        b = parseInt(match[3], 10);
      }
    }
    // Validate and parse RGBA color
    else if (/^rgba\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3}),\s*([0-1]?\.?\d*)\)$/.test(color)) {
      inputFormat = 'rgba';
      const match = color.match(/^rgba\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3}),\s*([0-1]?\.?\d*)\)$/);
      if (match) {
        r = parseInt(match[1], 10);
        g = parseInt(match[2], 10);
        b = parseInt(match[3], 10);
        a = parseFloat(match[4]);
      }
    } else {
      logger.error(`Invalid color format: ${color}`);
      return color; // Return original color on error
    }
  
    // Validate RGB values are in range [0, 255] and alpha is in [0, 1]
    if (r > 255 || g > 255 || b > 255 || r < 0 || g < 0 || b < 0 || a > 1 || a < 0) {
      logger.error(`Invalid color values: ${color}`);
      return color; // Return original color on error
    }
  
    // Reduce brightness, ensuring values stay in [0, 255]
    r = Math.floor(Math.max(0, Math.min(255, r * (1 - percent / 100))));
    g = Math.floor(Math.max(0, Math.min(255, g * (1 - percent / 100))));
    b = Math.floor(Math.max(0, Math.min(255, b * (1 - percent / 100))));
  
    // Return in the same format as input
    if (inputFormat === 'hex') {
      return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).padStart(6, '0')}`;
    } else if (inputFormat === 'hex-alpha') {
      const alphaHex = Math.round(a * 255).toString(16).padStart(2, '0');
      return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).padStart(6, '0')}${alphaHex}`;
    } else if (inputFormat === 'rgb') {
      return `rgb(${r}, ${g}, ${b})`;
    } else {
      return `rgba(${r}, ${g}, ${b}, ${a})`;
    }
}

export function lightenColor(color: string, percent: number): string {
  let r: number = 0, g: number = 0, b: number = 0, a: number = 1; // Default alpha is 1 (opaque)
  let inputFormat: 'hex' | 'hex-alpha' | 'rgb' | 'rgba' | 'invalid' = 'invalid';

  // Validate and parse hex color (6-digit)
  if (/^#[0-9A-Fa-f]{6}$/.test(color)) {
    inputFormat = 'hex';
    const hex: string = color.replace('#', '');
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  }
  // Validate and parse hex color with alpha (8-digit)
  else if (/^#[0-9A-Fa-f]{8}$/.test(color)) {
    inputFormat = 'hex-alpha';
    const hex: string = color.replace('#', '');
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
    a = parseInt(hex.slice(6, 8), 16) / 255; // Convert alpha to [0, 1]
  }
  // Validate and parse RGB color
  else if (/^rgb\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})\)$/.test(color)) {
    inputFormat = 'rgb';
    const match = color.match(/^rgb\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})\)$/);
    if (match) {
      r = parseInt(match[1], 10);
      g = parseInt(match[2], 10);
      b = parseInt(match[3], 10);
    }
  }
  // Validate and parse RGBA color
  else if (/^rgba\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3}),\s*([0-1]?\.?\d*)\)$/.test(color)) {
    inputFormat = 'rgba';
    const match = color.match(/^rgba\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3}),\s*([0-1]?\.?\d*)\)$/);
    if (match) {
      r = parseInt(match[1], 10);
      g = parseInt(match[2], 10);
      b = parseInt(match[3], 10);
      a = parseFloat(match[4]);
    }
  } else {
    logger.error(`Invalid color format: ${color}`);
    return color; // Return original color on error
  }

  // Validate RGB values are in range [0, 255] and alpha is in [0, 1]
  if (r > 255 || g > 255 || b > 255 || r < 0 || g < 0 || b < 0 || a > 1 || a < 0) {
    logger.error(`Invalid color values: ${color}`);
    return color; // Return original color on error
  }

  // Increase brightness, ensuring values stay in [0, 255]
  r = Math.floor(Math.max(0, Math.min(255, r + (255 - r) * (percent / 100))));
  g = Math.floor(Math.max(0, Math.min(255, g + (255 - g) * (percent / 100))));
  b = Math.floor(Math.max(0, Math.min(255, b + (255 - b) * (percent / 100))));

  // Return in the same format as input
  if (inputFormat === 'hex') {
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).padStart(6, '0')}`;
  } else if (inputFormat === 'hex-alpha') {
    const alphaHex = Math.round(a * 255).toString(16).padStart(2, '0');
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).padStart(6, '0')}${alphaHex}`;
  } else if (inputFormat === 'rgb') {
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
}

export function contrastColor(color: string, luminanceTreshold = 0.6): string {
  const luminance = new TinyColor(color).getLuminance();
  const contrastColor = luminance > 0.6 ? 'rgba(0,0,0,0.87)' : '#ffffff';
  return contrastColor
}

export function setAlpha(color: string, alpha: number): string {
  const c = new TinyColor(color);
  return c.setAlpha(alpha).toRgbString();
}